import { defineStore } from 'pinia'
import type { AddRecipeToShoppingResult } from '@refuelled/shared'
import { ref } from 'vue'
import { api, ApiError } from '@/api/client'
import { isNetworkError, readOffline, writeOffline } from '@/lib/offline'

export interface ShoppingItem {
  id: string
  foodId: string | null
  foodName: string | null
  freeText: string | null
  quantity: number | null
  unit: 'g' | 'ml' | 'serving' | null
  aisle: string | null
  checked: boolean
  addedToInventory: boolean
  note: string | null
  position: number
}

export interface ShoppingList {
  id: string
  name: string
  planId: string | null
  status: 'open' | 'done' | 'archived'
  items: ShoppingItem[]
}

export interface ShoppingSuggestion {
  foodId: string | null
  foodName: string | null
  freeText: string | null
  quantity: number | null
  unit: 'g' | 'ml' | 'serving' | null
  aisle: string | null
  lists: number
  lastAt: string
}

/**
 * A change made with no signal, waiting to be sent (N-06). Ticks and
 * removals are idempotent, so sending one twice does no harm; a line added
 * offline carries a temporary id until the server gives it a real one.
 */
export type QueuedChange =
  | { kind: 'check'; itemId: string; checked: boolean }
  | { kind: 'remove'; itemId: string }
  | { kind: 'add'; listId: string; tempId: string; freeText: string; checked: boolean }

interface Cached {
  lists: ListSummary[]
  current: ShoppingList | null
}

type ListSummary = { id: string; name: string; status: string; itemCount: number; checkedCount: number }

const TEMP = 'offline:'
export const isTempId = (id: string) => id.startsWith(TEMP)

export const useShoppingStore = defineStore('shopping', () => {
  const lists = ref<ListSummary[]>([])
  const current = ref<ShoppingList | null>(null)
  const error = ref<string | null>(null)
  /** The last attempt to reach the server failed for want of a connection. */
  const offline = ref(false)
  /** Changes made offline, in order, not yet sent. */
  const queue = ref<QueuedChange[]>([])
  let queueRead: Promise<void> | null = null

  function fail(err: unknown, fallback: string) {
    error.value = err instanceof ApiError ? err.message : fallback
  }

  /** The queue as it was left, once, before anything adds to it. */
  function readQueue(): Promise<void> {
    queueRead ??= readOffline<QueuedChange[]>('shopping.queue').then((kept) => {
      if (kept?.length) queue.value = [...kept, ...queue.value]
    })
    return queueRead
  }

  async function saveCache() {
    await writeOffline('shopping.cache', { lists: lists.value, current: current.value } satisfies Cached)
  }

  async function enqueue(change: QueuedChange) {
    await readQueue()
    // A later tick of the same line replaces an earlier one.
    if (change.kind === 'check') {
      queue.value = queue.value.filter((c) => !(c.kind === 'check' && c.itemId === change.itemId))
    }
    queue.value = [...queue.value, change]
    offline.value = true
    await writeOffline('shopping.queue', queue.value)
    await saveCache()
  }

  async function load() {
    await readQueue()
    try {
      lists.value = await api.get('/shopping-lists')
      offline.value = false
      await saveCache()
      if (queue.value.length) void flush()
    } catch (err) {
      if (isNetworkError(err)) {
        offline.value = true
        const cached = await readOffline<Cached>('shopping.cache')
        if (cached) lists.value = cached.lists
        return
      }
      fail(err, 'Could not load your lists')
    }
  }

  async function open(id: string) {
    try {
      current.value = await api.get<ShoppingList>(`/shopping-lists/${id}`)
      offline.value = false
      await saveCache()
    } catch (err) {
      if (isNetworkError(err)) {
        offline.value = true
        const cached = await readOffline<Cached>('shopping.cache')
        if (cached?.current?.id === id) current.value = cached.current
        return
      }
      fail(err, 'Could not open that list')
    }
  }

  /**
   * Send what was changed offline, in order. A change the server refuses
   * (the line was deleted on another phone, say) is dropped; losing the
   * connection again stops and keeps the rest for next time.
   */
  let flushing: Promise<void> | null = null
  function flush(): Promise<void> {
    flushing ??= sendQueued().finally(() => {
      flushing = null
    })
    return flushing
  }

  async function sendQueued() {
    await readQueue()
    while (queue.value.length) {
      const change = queue.value[0]!
      try {
        if (change.kind === 'check') {
          await api.patch(`/shopping-items/${change.itemId}`, { checked: change.checked })
        } else if (change.kind === 'remove') {
          await api.delete(`/shopping-items/${change.itemId}`)
        } else {
          const before = new Set((current.value?.items ?? []).map((i) => i.id))
          const list = await api.post<ShoppingList>(`/shopping-lists/${change.listId}/items`, {
            freeText: change.freeText,
          })
          const added = list.items.find((i) => !before.has(i.id) && i.freeText === change.freeText)
          if (added && change.checked) await api.patch(`/shopping-items/${added.id}`, { checked: true })
        }
      } catch (err) {
        if (isNetworkError(err)) {
          offline.value = true
          return
        }
        // Refused: nothing more to do with this one.
      }
      queue.value = queue.value.slice(1)
      await writeOffline('shopping.queue', queue.value)
    }
    offline.value = false
    if (current.value) {
      const id = current.value.id
      current.value = await api.get<ShoppingList>(`/shopping-lists/${id}`).catch(() => current.value)
    }
    lists.value = await api.get<ListSummary[]>('/shopping-lists').catch(() => lists.value)
    await saveCache()
  }

  /** A new list starts blank unless asked to bring the staples that are running low. */
  async function create(name: string, includeStaples = false): Promise<ShoppingList> {
    const list = await api.post<ShoppingList>('/shopping-lists', { name, includeStaples })
    current.value = list
    await load()
    return list
  }

  /** Everything off the list; the list itself stays. */
  async function clear(id: string) {
    await api.delete(`/shopping-lists/${id}/items`)
    if (current.value?.id === id) current.value.items = []
    await load()
  }

  /** Delete a list outright; the next open list, if any, takes its place on screen. */
  async function remove(id: string) {
    await api.delete(`/shopping-lists/${id}`)
    lists.value = lists.value.filter((l) => l.id !== id)
    if (current.value?.id === id) {
      current.value = null
      const next = lists.value.find((l) => l.status === 'open')
      if (next) await open(next.id)
    }
  }

  /**
   * Ticking flips locally first so the aisle feels instant, then reconciles.
   * A failure puts the tick back rather than leaving a lie on screen.
   */
  async function check(itemId: string, checked: boolean) {
    const item = current.value?.items.find((i) => i.id === itemId)
    if (!item) return
    const previous = item.checked
    item.checked = checked
    if (isTempId(itemId)) {
      // Not on the server yet: the tick travels with the line when it goes.
      await readQueue()
      queue.value = queue.value.map((c) => (c.kind === 'add' && c.tempId === itemId ? { ...c, checked } : c))
      await writeOffline('shopping.queue', queue.value)
      await saveCache()
      return
    }
    try {
      await api.patch(`/shopping-items/${itemId}`, { checked })
      await saveCache()
    } catch (err) {
      if (isNetworkError(err)) return enqueue({ kind: 'check', itemId, checked })
      item.checked = previous
      fail(err, 'That did not save')
    }
  }

  /**
   * Offline, a typed line goes on the list straight away with a temporary
   * id and is sent later. A food picked from the library needs the server,
   * so that still fails without a connection.
   */
  async function addItem(listId: string, input: Record<string, unknown>) {
    try {
      current.value = await api.post<ShoppingList>(`/shopping-lists/${listId}/items`, input)
      await saveCache()
    } catch (err) {
      const freeText = typeof input.freeText === 'string' ? input.freeText : null
      if (!isNetworkError(err) || !freeText || input.foodId || current.value?.id !== listId) throw err
      const tempId = `${TEMP}${crypto.randomUUID()}`
      current.value.items = [
        ...current.value.items,
        {
          id: tempId,
          foodId: null,
          foodName: null,
          freeText,
          quantity: null,
          unit: null,
          aisle: null,
          checked: false,
          addedToInventory: false,
          note: null,
          position: current.value.items.length,
        },
      ]
      await enqueue({ kind: 'add', listId, tempId, freeText, checked: false })
    }
  }

  /** Things bought before that are not on this list yet, most-bought first. */
  /** A recipe's missing ingredients onto the open list (M-90), for the servings asked. */
  async function addRecipe(recipeId: string, servings?: number | null): Promise<AddRecipeToShoppingResult> {
    const result = await api.post<AddRecipeToShoppingResult>('/shopping-lists/from-recipe', { recipeId, servings: servings ?? null })
    await load()
    return result
  }

  async function suggestions(listId: string, limit = 50): Promise<ShoppingSuggestion[]> {
    return api.get<ShoppingSuggestion[]>(`/shopping-lists/${listId}/suggestions`, { limit })
  }

  /** Several lines at once; the list on screen is the state after the last. */
  async function addItems(listId: string, inputs: Array<Record<string, unknown>>) {
    let latest: ShoppingList | null = null
    for (const input of inputs) {
      latest = await api.post<ShoppingList>(`/shopping-lists/${listId}/items`, input)
    }
    if (latest) current.value = latest
  }

  async function removeItem(itemId: string) {
    const drop = () => {
      if (current.value) current.value.items = current.value.items.filter((i) => i.id !== itemId)
    }
    if (isTempId(itemId)) {
      await readQueue()
      queue.value = queue.value.filter((c) => !(c.kind === 'add' && c.tempId === itemId))
      await writeOffline('shopping.queue', queue.value)
      drop()
      return saveCache()
    }
    try {
      await api.delete(`/shopping-items/${itemId}`)
    } catch (err) {
      if (!isNetworkError(err)) throw err
      drop()
      return enqueue({ kind: 'remove', itemId })
    }
    drop()
    await saveCache()
  }

  async function putAway(listId: string, locationId: string) {
    const result = await api.post<{ added: number; skipped: number }>(
      `/shopping-lists/${listId}/to-inventory`,
      { locationId },
    )
    await open(listId)
    return result
  }

  return {
    lists,
    current,
    error,
    offline,
    queue,
    flush,
    load,
    open,
    create,
    remove,
    clear,
    check,
    addItem,
    addItems,
    suggestions,
    addRecipe,
    removeItem,
    putAway,
  }
})
